import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import {
  ArrowRight,
  Download,
  RefreshCw,
  Sparkles,
  Palette,
  Users,
  Beaker,
  Wrench,
  Briefcase,
  Calculator,
} from 'lucide-react';

type HollandType =
  | 'artistic'
  | 'social'
  | 'investigative'
  | 'realistic'
  | 'enterprising'
  | 'conventional';

const hollandColors: Record<HollandType, { base: string; bg: string; border: string }> = {
  artistic: { base: '#F97316', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.25)' },
  social: { base: '#16a34a', bg: 'rgba(22,163,74,0.12)', border: 'rgba(22,163,74,0.25)' },
  investigative: { base: '#0369A1', bg: 'rgba(3,105,161,0.12)', border: 'rgba(3,105,161,0.25)' },
  realistic: { base: '#0EA5E9', bg: 'rgba(14,165,233,0.12)', border: 'rgba(14,165,233,0.25)' },
  enterprising: { base: '#9333EA', bg: 'rgba(147,51,234,0.12)', border: 'rgba(147,51,234,0.25)' },
  conventional: { base: '#DC2626', bg: 'rgba(220,38,38,0.12)', border: 'rgba(220,38,38,0.25)' },
};

const hollandIcons: Record<HollandType, React.ReactNode> = {
  artistic: <Palette className='w-8 h-8' />,
  social: <Users className='w-8 h-8' />,
  investigative: <Beaker className='w-8 h-8' />,
  realistic: <Wrench className='w-8 h-8' />,
  enterprising: <Briefcase className='w-8 h-8' />,
  conventional: <Calculator className='w-8 h-8' />,
};

const hollandData: Array<{
  key: HollandType;
  label: string;
  score: number;
  total: number;
  angle: number;
  rank: number;
}> = [
  { key: 'artistic', label: 'Nghệ thuật', score: 35, total: 40, angle: 0, rank: 1 },
  { key: 'social', label: 'Xã hội', score: 32, total: 40, angle: 60, rank: 2 },
  { key: 'investigative', label: 'Nghiên cứu', score: 28, total: 40, angle: 120, rank: 3 },
  { key: 'realistic', label: 'Kỹ thuật', score: 24, total: 40, angle: 180, rank: 4 },
  { key: 'enterprising', label: 'Quản lý', score: 20, total: 40, angle: 240, rank: 5 },
  { key: 'conventional', label: 'Nghiệp vụ', score: 10, total: 40, angle: 300, rank: 6 },
];

const topProfiles = [
  {
    key: 'artistic' as HollandType,
    title: 'Nghệ thuật (Artistic)',
    score: '35/40',
    descriptionTitle: 'Mô tả chung',
    description: [
      'Sáng tạo và tự do: yêu thích sự tự do trong suy nghĩ và hành động, thường có ý tưởng khác biệt.',
      'Trực giác mạnh: ra quyết định dựa trên cảm xúc và trực giác hơn là quy tắc cứng.',
      'Ghét sự gò bó: môi trường lặp lại/quá nhiều quy tắc khiến bạn ngột ngạt.',
      'Thể hiện bản thân: cần thể hiện cái tôi qua sản phẩm sáng tạo.',
    ],
    careersTitle: 'Gợi ý ngành nghề phù hợp',
    careers: [
      'Thiết kế đồ họa / Mỹ thuật đa phương tiện',
      'Kiến trúc sư / Thiết kế nội thất',
      'Biên tập viên / Nhà văn / Content Creator',
    ],
  },
  {
    key: 'social' as HollandType,
    title: 'Xã hội (Social)',
    score: '32/40',
    descriptionTitle: 'Mô tả chung',
    description: [
      'Thích giúp đỡ: tìm thấy niềm vui khi hỗ trợ, chăm sóc hoặc giảng dạy.',
      'Kỹ năng giao tiếp: dễ kết nối, lắng nghe và thấu hiểu.',
      'Hướng ngoại, thân thiện: thích làm việc nhóm và hoạt động cộng đồng.',
    ],
    careersTitle: 'Gợi ý ngành nghề phù hợp',
    careers: [
      'Giáo viên / Giảng viên',
      'Bác sĩ / Điều dưỡng / Chăm sóc sức khỏe',
      'Công tác xã hội / Tâm lý học',
    ],
  },
  {
    key: 'investigative' as HollandType,
    title: 'Nghiên cứu (Investigative)',
    score: '28/40',
    descriptionTitle: 'Mô tả chung',
    description: [
      'Tư duy phân tích: thích tìm hiểu nguyên lý và giải quyết vấn đề phức tạp.',
      'Thích dữ liệu và thí nghiệm: ưu tiên môi trường có bằng chứng, số liệu.',
      'Tò mò, đặt câu hỏi: luôn tìm nguyên nhân gốc rễ trước khi kết luận.',
    ],
    careersTitle: 'Gợi ý ngành nghề phù hợp',
    careers: [
      'Khoa học dữ liệu / Phân tích',
      'Nghiên cứu khoa học / Kỹ thuật',
      'Y khoa / Dược / Sinh học',
    ],
  },
];

const patternStyles: Record<HollandType, React.CSSProperties> = {
  artistic: {
    backgroundImage:
      'radial-gradient(circle at top left, rgba(249,115,22,0.25) 0%, transparent 65%), radial-gradient(circle at bottom right, rgba(249,115,22,0.25) 0%, transparent 65%)',
  },
  social: {
    backgroundImage:
      'radial-gradient(circle at top right, rgba(22,163,74,0.25) 0%, transparent 65%), radial-gradient(circle at bottom left, rgba(22,163,74,0.25) 0%, transparent 65%)',
  },
  investigative: {
    backgroundImage: 'radial-gradient(circle at center, rgba(3,105,161,0.25) 0%, transparent 65%)',
  },
  realistic: {
    backgroundImage: 'radial-gradient(circle at center, rgba(14,165,233,0.25) 0%, transparent 65%)',
  },
  enterprising: {
    backgroundImage: 'radial-gradient(circle at center, rgba(147,51,234,0.25) 0%, transparent 65%)',
  },
  conventional: {
    backgroundImage: 'radial-gradient(circle at center, rgba(220,38,38,0.25) 0%, transparent 65%)',
  },
};

const getCompassPosition = (angle: number, radius = 170) => {
  const rad = (angle - 90) * (Math.PI / 180); // start from top
  const x = radius * Math.cos(rad);
  const y = radius * Math.sin(rad);
  return {
    left: `calc(50% + ${x}px)`,
    top: `calc(50% + ${y}px)`,
  };
};

export function FullReportPage() {
  return (
    <div className='bg-gray-50 dark:bg-background-dark text-text-light dark:text-text-dark min-h-screen flex flex-col'>
      <SEOHead
        title='Báo cáo hướng nghiệp | Hướng nghiệp CAT'
        description='Báo cáo chi tiết kết quả trắc nghiệm hướng nghiệp CAT.'
        noindex
      />
      <UnifiedHeader />
      <main className='pt-16 pb-12 flex-1'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10'>
          {/* Hero */}
          <section className='relative overflow-hidden rounded-2xl border border-purple-200 dark:border-purple-800 shadow-xl bg-gradient-to-br from-purple-100 to-indigo-200 dark:from-purple-950 dark:to-indigo-900 p-8 sm:p-10'>
            <div className='absolute inset-0 pointer-events-none bg-gradient-to-br from-purple-100/40 to-indigo-100/40 dark:from-purple-900/30 dark:to-indigo-900/30' />
            <div className='relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
              <div className='space-y-4 max-w-3xl'>
                <p className='inline-flex items-center gap-2 text-sm font-semibold text-purple-800 dark:text-purple-200 bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 px-3 py-1 rounded-full w-fit shadow-sm'>
                  <Sparkles className='w-4 h-4' />
                  Bảng tổng hợp kết quả
                </p>
                <h1 className='text-4xl sm:text-5xl font-extrabold text-text-light dark:text-white leading-tight'>
                  Báo cáo kết quả tự đánh giá và tư vấn hướng nghiệp
                </h1>
                <p className='text-lg text-subtext-light dark:text-subtext-dark'>
                  Họ và tên:{' '}
                  <span className='font-semibold text-text-light dark:text-white'>
                    Nguyễn Văn A
                  </span>{' '}
                  <span className='mx-2 text-gray-400'>•</span> Ngày làm bài: 02/11/2024{' '}
                  <span className='mx-2 text-gray-400'>•</span> Mã báo cáo: CAT-2024-11-02-001
                </p>
                <div className='flex flex-wrap gap-3'>
                  <button className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-semibold shadow-sm hover:shadow transition'>
                    <Download className='w-4 h-4' />
                    Tải PDF
                  </button>
                  <button className='inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/60 text-white font-semibold hover:bg-white/10 transition'>
                    <RefreshCw className='w-4 h-4' />
                    Làm lại từ đầu
                  </button>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4 bg-white/70 dark:bg-white/5 backdrop-blur border border-white/60 dark:border-white/10 rounded-2xl p-4 text-sm font-semibold min-w-[260px]'>
                <div className='flex flex-col gap-1'>
                  <span className='text-subtext-light dark:text-subtext-dark'>Thời gian</span>
                  <span className='text-lg text-text-light dark:text-white'>38 phút</span>
                </div>
                <div className='flex flex-col gap-1'>
                  <span className='text-subtext-light dark:text-subtext-dark'>Tổng câu</span>
                  <span className='text-lg text-text-light dark:text-white'>180</span>
                </div>
                <div className='flex flex-col gap-1'>
                  <span className='text-subtext-light dark:text-subtext-dark'>Điểm cao nhất</span>
                  <span className='text-lg text-orange-600 dark:text-orange-400'>35/40</span>
                </div>
                <div className='flex flex-col gap-1'>
                  <span className='text-subtext-light dark:text-subtext-dark'>Cấp độ</span>
                  <span className='text-lg text-text-light dark:text-white'>THPT</span>
                </div>
              </div>
            </div>
          </section>

          {/* Holland Compass */}
          <section className='bg-white dark:bg-card-dark rounded-2xl shadow border border-gray-100 dark:border-gray-800 p-6 lg:p-8 relative overflow-hidden'>
            <div className='flex items-center gap-2 mb-6'>
              <span className='material-symbols-outlined text-amber-500 text-3xl'>explore</span>
              <div>
                <p className='text-sm text-subtext-light dark:text-subtext-dark font-semibold uppercase'>
                  La bàn tính cách
                </p>
                <h2 className='text-3xl font-bold'>Tổng quan 6 nhóm (Lý thuyết Holland)</h2>
              </div>
            </div>
            <div className='grid grid-cols-1 xl:grid-cols-12 gap-8 items-center'>
              <div className='xl:col-span-7'>
                <div className='relative bg-gradient-to-br from-gray-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-inner min-h-[420px] flex items-center justify-center overflow-hidden'>
                  <div className='absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_30%_30%,rgba(249,115,22,0.08),transparent_50%)]' />
                  <div className='absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_70%_70%,rgba(3,105,161,0.08),transparent_55%)]' />
                  <div className='relative w-full h-[420px]'>
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <div className='w-64 h-64 rounded-full border-2 border-dashed border-gray-200 dark:border-gray-700' />
                    </div>
                    {hollandData.map(item => {
                      const pos = getCompassPosition(item.angle);
                      const isTop3 = item.rank <= 3;
                      return (
                        <div
                          key={item.key}
                          className='absolute flex items-center justify-center'
                          style={pos}
                        >
                          <div
                            className='relative flex flex-col items-center justify-center rounded-full shadow-lg text-center'
                            style={{
                              width: isTop3 ? 104 : 88,
                              height: isTop3 ? 104 : 88,
                              backgroundColor: hollandColors[item.key].bg,
                              border: `1px solid ${hollandColors[item.key].border}`,
                              color: hollandColors[item.key].base,
                            }}
                          >
                            <div className='absolute inset-0 rounded-full opacity-15' />
                            <div
                              className='w-full h-full flex flex-col items-center justify-center gap-1'
                              style={{ transform: `rotate(-${item.angle}deg)` }}
                            >
                              <div className='flex items-center justify-center'>
                                {hollandIcons[item.key]}
                              </div>
                              <div className='text-[11px] font-semibold uppercase px-2 leading-tight text-center'>
                                {item.label}
                              </div>
                              <div className='text-sm font-bold'>
                                {item.score}
                                <span className='text-[11px] font-medium opacity-70'>
                                  /{item.total}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <div className='text-center space-y-1 bg-white dark:bg-slate-800 px-4 py-3 rounded-xl shadow border border-gray-100 dark:border-gray-700'>
                        <p className='text-sm font-semibold text-subtext-light dark:text-subtext-dark'>
                          Nhóm mạnh nhất
                        </p>
                        <p className='text-xl font-bold text-orange-600 dark:text-orange-400'>
                          A - S - I
                        </p>
                        <p className='text-xs text-subtext-light dark:text-subtext-dark'>
                          Nghệ thuật • Xã hội • Nghiên cứu
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='xl:col-span-5 space-y-4'>
                <div className='flex items-center gap-2 text-sm text-subtext-light dark:text-subtext-dark'>
                  <Sparkles className='w-4 h-4 text-orange-500' />
                  Top 3 được làm nổi bật với kích thước lớn hơn và màu nhấn mạnh.
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  {hollandData.map(item => (
                    <div
                      key={item.key}
                      className='flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-slate-900 border-gray-100 dark:border-gray-800 shadow-sm'
                    >
                      <div
                        className='w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold'
                        style={{ backgroundColor: hollandColors[item.key].base }}
                      >
                        {item.rank}
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-sm font-semibold'>{item.label}</span>
                        <span className='text-xs text-subtext-light dark:text-subtext-dark'>
                          {item.score}/{item.total} điểm
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Top 3 profiles */}
          <section className='space-y-4'>
            <div className='flex items-center gap-2'>
              <span className='material-symbols-outlined text-amber-500 text-3xl'>stars</span>
              <h2 className='text-3xl font-bold'>Top 3 nhóm tính cách mạnh nhất</h2>
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
              {topProfiles.map(profile => (
                <div
                  key={profile.key}
                  className='relative bg-card-light dark:bg-card-dark rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xl p-6 flex flex-col'
                >
                  <div
                    className='absolute inset-0 rounded-2xl opacity-60'
                    style={patternStyles[profile.key]}
                  />
                  <div className='relative z-10 flex-1 space-y-6'>
                    <div className='flex items-center gap-4'>
                      <div
                        className='flex items-center justify-center w-14 h-14 rounded-full text-white font-bold text-2xl shadow-lg'
                        style={{ backgroundColor: hollandColors[profile.key].base }}
                      >
                        {topProfiles.indexOf(profile) + 1}
                      </div>
                      <div>
                        <h3
                          className='text-2xl font-bold uppercase'
                          style={{ color: hollandColors[profile.key].base }}
                        >
                          {profile.title}
                        </h3>
                        <div
                          className='text-sm font-semibold px-4 py-1 rounded-full inline-block mt-2 shadow-sm'
                          style={{
                            backgroundColor: hollandColors[profile.key].base,
                            color: '#fff',
                          }}
                        >
                          Điểm: {profile.score}
                        </div>
                      </div>
                    </div>

                    <div className='space-y-3'>
                      <div
                        className='flex items-center gap-2'
                        style={{ color: hollandColors[profile.key].base }}
                      >
                        <span className='material-symbols-outlined text-xl'>description</span>
                        <h4 className='font-bold text-lg uppercase'>{profile.descriptionTitle}</h4>
                      </div>
                      <ul className='list-disc list-outside pl-5 space-y-2 text-sm text-subtext-light dark:text-subtext-dark'>
                        {profile.description.map(item => (
                          <li key={item} className='marker:text-current'>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div
                    className='relative z-10 border-t border-dashed pt-6 mt-6'
                    style={{ borderColor: `${hollandColors[profile.key].base}40` }}
                  >
                    <div
                      className='flex items-center gap-2 mb-3'
                      style={{ color: hollandColors[profile.key].base }}
                    >
                      <span className='material-symbols-outlined text-xl'>work</span>
                      <h4 className='font-bold text-lg uppercase'>{profile.careersTitle}</h4>
                    </div>
                    <p className='text-xs text-subtext-light dark:text-subtext-dark mb-3 italic'>
                      * Các ngành nghề được sắp xếp theo mức độ phù hợp từ cao xuống thấp.
                    </p>
                    <div className='space-y-2'>
                      {profile.careers.map((career, idx) => (
                        <div
                          key={career}
                          className='flex items-center gap-3 p-3 rounded-lg border text-sm font-medium'
                          style={{
                            backgroundColor: hollandColors[profile.key].bg,
                            borderColor: hollandColors[profile.key].border,
                            color: hollandColors[profile.key].base,
                          }}
                        >
                          <span className='w-6 h-6 rounded-full bg-white/70 text-center text-xs font-bold text-text-light flex items-center justify-center shadow'>
                            {idx + 1}
                          </span>
                          <span className='text-text-light dark:text-white'>{career}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Next steps */}
          <section className='bg-white dark:bg-card-dark rounded-2xl shadow border border-gray-100 dark:border-gray-800 p-6 lg:p-8'>
            <div className='flex items-center gap-2 mb-4'>
              <ArrowRight className='w-5 h-5 text-orange-500' />
              <h3 className='text-xl font-bold'>Bước tiếp theo - Bạn nên làm gì?</h3>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              {[
                {
                  title: 'Thực hành chuyên sâu',
                  desc: 'Chọn 1-2 bài test bổ sung theo nhóm mạnh nhất để khẳng định kết quả.',
                },
                {
                  title: 'Khám phá ngành học',
                  desc: 'Tìm hiểu chương trình đào tạo, môi trường học tập của 3 ngành được gợi ý.',
                },
                {
                  title: 'Lập kế hoạch 90 ngày',
                  desc: 'Xây dựng checklist hoạt động rèn luyện kỹ năng phù hợp nhóm A/S/I.',
                },
              ].map(item => (
                <div
                  key={item.title}
                  className='p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-slate-900'
                >
                  <p className='text-sm font-semibold text-orange-600 mb-1'>{item.title}</p>
                  <p className='text-sm text-subtext-light dark:text-subtext-dark'>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <UnifiedFooter />
    </div>
  );
}
